import React from 'react';

import pluralize from 'pluralize';

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { ValidationResult } from './types';

interface Props {
  validationResult: ValidationResult;
  isCommitting: boolean;
  onCommit: () => void;
  onNewUpload: () => void;
  onConfirmPartialCommit: () => void;
}

export default function ValidationActions({
  validationResult,
  isCommitting,
  onCommit,
  onNewUpload,
  onConfirmPartialCommit,
}: Props) {
  const isFormatPhase = validationResult.phase === 'format';
  const canPartialCommit = validationResult.phase === 'database' && validationResult.errorCount > 0;
  const allValid = !isFormatPhase && !canPartialCommit && validationResult.errorCount === 0;

  return (
    <Box mt={3}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
        {isFormatPhase && (
          <Typography variant="body2" color="error" sx={{ flex: 1 }}>
            Format errors found. Please fix the file and re-upload.
          </Typography>
        )}
        {canPartialCommit && (
          <Typography variant="body2" color="warning.main" sx={{ flex: 1 }}>
            {pluralize('row', validationResult.errorCount, true)} have database errors. {validationResult.validCount}{' '}
            valid rows can be committed.
          </Typography>
        )}
        {allValid && (
          <Typography variant="body2" color="success.main" sx={{ flex: 1 }}>
            All {validationResult.validCount} rows passed validation and are ready to commit.
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onNewUpload} disabled={isCommitting}>
            {isFormatPhase || canPartialCommit ? 'Fix & Re-upload' : 'Cancel'}
          </Button>
          {!isFormatPhase && validationResult.validCount > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={canPartialCommit ? onConfirmPartialCommit : onCommit}
              disabled={isCommitting}
            >
              {isCommitting && <CircularProgress size={20} sx={{ mr: 1 }} />}
              {isCommitting
                ? 'Committing...'
                : canPartialCommit
                ? `Commit ${validationResult.validCount} Valid Rows`
                : 'Commit'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
